import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { FileText, History } from "lucide-react"
import { useRef, useState } from "react"

import { api, pedirBinario } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { comoFechaHora } from "@/lib/fechas"

import type { PaginaImpresiones } from "./tipos"

/** Cuántas impresiones se ven por página. La paginación es del servidor. */
const POR_PAGINA = 10

/**
 * El registro de las solicitudes de posicionamiento impresas: cuándo, quién y el PDF.
 *
 * ⚠ **«Ver PDF» abre el archivo GUARDADO y no recompone nada.** Es el PDF que salió
 * por la impresora, byte a byte. Si la aduana cambia el formato el año que viene, lo
 * impreso antes se sigue viendo como se entregó; recomponerlo desde los datos con la
 * plantilla de hoy sería enseñar un papel que nunca existió.
 *
 * ⚠ **No hay botón de borrar.** Es un registro de hechos: ver
 * `services/impresiones.py`.
 */
export function RegistroImpresiones() {
  const [desde, setDesde] = useState(0)
  const [fallo, setFallo] = useState<string | null>(null)

  const pagina = useQuery({
    queryKey: ["impresiones", { formato: "POSICIONAMIENTO", desde }],
    queryFn: () =>
      api.get<PaginaImpresiones>(
        `/api/v1/impresiones?formato=POSICIONAMIENTO&limit=${POR_PAGINA}&offset=${desde}`,
      ),
    // Al pasar de página se queda la anterior a la vista hasta que llega la nueva, en
    // vez de vaciar la tabla y hacerla saltar.
    placeholderData: keepPreviousData,
  })

  /**
   * ⚠ **La pestaña se abre ANTES de pedir el archivo, y vacía.** Abierta después del
   * `await` llegaría fuera del gesto del clic, y el bloqueador de ventanas emergentes
   * se la come sin avisar: el botón parecería no hacer nada.
   */
  async function verPdf(id: number) {
    setFallo(null)
    const pestana = window.open("", "_blank")
    if (pestana === null) {
      setFallo(
        "El navegador no dejó abrir una pestaña nueva: permite las ventanas emergentes de este sitio.",
      )
      return
    }
    try {
      const url = URL.createObjectURL(await pedirBinario(`/api/v1/impresiones/${id}/pdf`))
      pestana.location.href = url
      // Se suelta cuando la pestaña ya lo cargó; hacerlo al instante la dejaría en blanco.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (error) {
      pestana.close()
      setFallo(error instanceof Error ? error.message : "No se pudo abrir el PDF.")
    }
  }

  const total = pagina.data?.total ?? 0
  const filas = pagina.data?.items ?? []
  const vista = useRef<HTMLDivElement | null>(null)

  return (
    <section className="no-print mt-8" aria-labelledby="titulo-registro-impresiones">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2
          id="titulo-registro-impresiones"
          className="flex items-center gap-2 text-base font-semibold"
        >
          <History size={18} aria-hidden={true} />
          Solicitudes impresas
        </h2>
        {filas.length > 0 && (
          <Exportar
            titulo="Solicitudes de posicionamiento impresas"
            subtitulo={`${desde + 1}–${desde + filas.length} de ${total}`}
            contenido={vista}
          />
        )}
      </div>
      <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
        Cada vez que se imprime una solicitud queda aquí, con el PDF exacto que salió.
      </p>

      {fallo !== null && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {fallo}
        </p>
      )}

      {pagina.isPending ? (
        <p className="mt-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando el registro…
        </p>
      ) : pagina.isError ? (
        <p role="alert" className="mt-4 text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el registro: {pagina.error.message}
        </p>
      ) : total === 0 ? (
        <p className="mt-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no se ha impreso ninguna solicitud desde el sistema.
        </p>
      ) : (
        <>
          {/* La tabla se desplaza DENTRO de su caja en un teléfono, nunca la página. */}
          <div ref={vista} className="mt-4 overflow-x-auto">
            <table
              className="w-full min-w-[34rem] text-left text-sm"
              data-exportar-titulo="Solicitudes impresas"
            >
              <thead style={{ color: "var(--fg-muted)" }}>
                <tr>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Impresa
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    N° de DUA
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    N° BL
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Quién
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    <span className="sr-only">PDF</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => (
                  <tr key={fila.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                    <td className="whitespace-nowrap py-2 pr-4 tabular-nums">
                      {comoFechaHora(fila.creado_en)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{fila.dua || "—"}</td>
                    <td className="py-2 pr-4">{fila.bl || "—"}</td>
                    <td className="py-2 pr-4">{fila.creado_por_nombre ?? "—"}</td>
                    <td className="py-1 text-right">
                      <Boton
                        variante="fantasma"
                        onClick={() => void verPdf(fila.id)}
                        aria-label={`Ver el PDF de la solicitud impresa el ${comoFechaHora(fila.creado_en)}`}
                      >
                        <FileText size={16} aria-hidden={true} />
                        Ver PDF
                      </Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm"
            style={{ color: "var(--fg-muted)" }}
          >
            <span className="tabular-nums">
              {desde + 1}–{desde + filas.length} de {total}
            </span>
            <div className="flex gap-2">
              <Boton
                onClick={() => setDesde(Math.max(0, desde - POR_PAGINA))}
                disabled={desde === 0}
              >
                Más recientes
              </Boton>
              <Boton
                onClick={() => setDesde(desde + POR_PAGINA)}
                disabled={desde + POR_PAGINA >= total}
              >
                Anteriores
              </Boton>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
